import ItemsPerPage from '@shared/components/ItemsPerPage';
import SearchButton from '@shared/components/SearchButton';
import Breadcrumb from '@shared/components/breadcrumb/Breadcrumb';
import TitlePage from '@shared/components/titlePage/TitlePage';
import { CircleSpin, UserIcon } from 'assets/icons/Index';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import { bookRes } from 'types/data.type';
import StudentManagementService from '@core/services/student.portal.service';
import PaginationOwn from '@shared/components/Pagination';
import { AiOutlineEdit } from 'react-icons/ai';
import { PortalButton } from '@shared/components/button/PortalButton';
import DeleteStudentButton from './DeleteStudentButton';
import { toast } from 'react-toastify';

const StudentManagement = () => {
  const navigate = useNavigate();
  const [crumbs] = useState([
    { name: 'Student Management', url: '/portal/student-management' }
  ]);
  const [keyword, setKeyword] = useState<string>();
  const [reloadPage, setReloadPage] = useState<any>();
  const [itemPerPage, setItemPerPage] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState<bookRes>();
  const [searchParams] = useSearchParams();
  const page = searchParams.get('page') || 1;
  const [status, setStatus] = useState<boolean>();
  const requestVersion = useRef(0);

  const selected = (url: any) => {
    if (url) {
      navigate(url);
    } else {
      navigate(-1);
    }
  };

  const getData = async (params: any) => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const res = await StudentManagementService.getAllStudents(params);
      if (version !== requestVersion.current) return;
      if (res?.status === 1) {
        const lastPage = Math.max(1, Math.ceil(res.data.total_results / params.page_size));
        if (params.page_index > lastPage) {
          navigate(`/portal/student-management?page=${lastPage}`, { replace: true });
        } else {
          setStudentData(res.data);
        }
      } else {
        toast.error(res?.message || 'Unable to load students. Please try again.');
      }
    } catch {
      if (version === requestVersion.current) toast.error('Unable to load students. Please try again.');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (page) {
      setCurrentPage(Number(page));
      let params = {
        search_key: '',
        is_active: null as any,
        page_index: Number(page),
        page_size: itemPerPage
      };
      if (keyword) {
        params = {
          ...params,
          search_key: keyword?.trim()
        };
      }
      if (status !== undefined) {
        params = {
          ...params,
          is_active: status
        };
      }

      getData(params);
    }
  }, [itemPerPage, page, status, reloadPage]);

  const onSearch = (reset?: boolean) => {
    navigate(`/portal/student-management`);
    const newKeywork = reset ? '' : keyword?.trim();
    setKeyword(newKeywork);
    setReloadPage(!reloadPage);
  };

  const _handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      setKeyword(e.target.value);
      onSearch();
    }
  };

  const handlePageSizeChange = (value: any) => {
    navigate(`/portal/student-management`);
    setItemPerPage(value);
  };

  const onPageChange = useCallback(async (event: number) => {
    navigate(`/portal/student-management?page=${event}`);
  }, []);

  const handleStudentStatusChange = (value: any) => {
    setStatus(value === '' ? undefined : value === 'true');
    navigate('/portal/student-management');
  };

  return (
    <>
      <Breadcrumb crumbs={crumbs} selected={selected} />
      <div className="flex items-center justify-between">
        <TitlePage icon={() => <UserIcon />} name="Student Management" />
        <div className="flex">
          <div className="mr-3">
            <PortalButton url="/portal/student-management/add-student">
              Add Student
            </PortalButton>
          </div>
        </div>
      </div>
      <div className="mt-7">
        <div className="flex">
          <div className="flex  w-1/2 space-x-2">
            <div className="mt-3">
              <SearchButton
                onSearch={onSearch}
                keyword={keyword}
                setKeyword={setKeyword}
                _handleKeyDown={_handleKeyDown}
              />
            </div>
            <div className="flex flex-col justify-between">
              <label
                htmlFor="student_status"
                className="text-sm font-semibold leading-5"
              >
                Student Status
              </label>
              <select
                name="student_status"
                className="w-[171px] h-10 border border-[#E5E5E5] rounded-2xl text-sm font-normal leading-5 px-[8px]"
                // value={authContext?.articleFilterParams?.article_type}
                onChange={value =>
                  handleStudentStatusChange(value?.target?.value)
                }
              >
                <option value="">---</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div className="ml-auto mt-5">
            <ItemsPerPage
              choice={itemPerPage}
              setChoice={(val: number) => {
                handlePageSizeChange(val);
              }}
            />
          </div>
        </div>
        <div className="w-full h-auto relative mt-2 overflow-auto rounded shadow">
          <table className="table w-full rounded-lg">
            <thead className="bg-gray-f2 border-b-2 border-gray-200">
              <tr>
                <th>STT</th>
                <th scope="col">
                  <span>Full Name</span>
                </th>
                <th scope="col">
                  <span>Gender</span>
                </th>
                <th scope="col">
                  <span>Email</span>
                </th>
                <th scope="col">
                  <span>Phone Number</span>
                </th>
                <th scope="col">
                  <span>Parent Name</span>
                </th>
                <th>
                  <span>Parent Phone</span>
                </th>
                <th scope="col">
                  <span>Account Type</span>
                </th>
                <th scope="col">
                  <span>Status</span>
                </th>
                <th scope="col">
                  <span>Action</span>
                </th>
              </tr>
            </thead>
            {!loading && (
              <tbody>
                {studentData?.items?.map((item: any, index: number) => {
                  return (
                    <tr key={item?.id}>
                      <th>{(currentPage - 1) * itemPerPage + index + 1}</th>
                      <td className="order">
                        <span>{item?.fullname}</span>
                      </td>
                      <td>
                        <span>{item?.gender}</span>
                      </td>
                      <td className="order">
                        <span>{item?.email}</span>
                      </td>
                      <td>
                        <span>{item?.phone}</span>
                      </td>
                      <td className="order">
                        <span>{item?.parent || '-'}</span>
                      </td>
                      <td className="order">
                        <span>{item?.parent_phone || '-'}</span>
                      </td>
                      <td className="order">
                        <span>{item?.type || '-'}</span>
                      </td>
                      <td>
                        <span
                          className={`status status-${
                            item?.is_active ? 'active' : 'inactive'
                          }`}
                        >
                          {item?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="flex h-8 items-center gap-2 whitespace-nowrap">
                          <Link to={`edit-student/${item.id}`} aria-label={`Edit ${item.fullname || item.username}`} title="Edit student">
                            <div className="table-action-btn table-action-edit w-fit">
                              <AiOutlineEdit />
                            </div>
                          </Link>
                          <DeleteStudentButton student={item}
                            onDeleted={() => setReloadPage((value: boolean) => !value)}
                            onRejected={() => setReloadPage((value: boolean) => !value)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {studentData?.items?.length === 0 && !loading && (
          <p className="text-center text-sm mt-3">No account found</p>
        )}

        {studentData &&
          studentData?.total_results > itemPerPage &&
          !loading && (
            <div className="my-6 flex text-right justify-center">
              <PaginationOwn
                totalItems={studentData?.total_results}
                itemPerPage={itemPerPage}
                pageChange={onPageChange}
                pageCurrent={currentPage}
              />
            </div>
          )}
        {loading && (
          <>
            <div className="flex justify-center mt-4 items-center tex-sm">
              <CircleSpin color="text-primary-e2" /> Loading...
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default StudentManagement;
